import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { EGGS, EGG_FAMILIES, HATCHABLE_EGGS, type EggSize } from "@/lib/eggs-data";
import { EggArt } from "@/components/EggArt";
import { ShareButton } from "@/components/ShareButton";
import { FaqSection, faqSchema, RelatedGuides, type FaqItem } from "@/components/PageFaq";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";

const URL = "https://palstars.com/eggs";

const FAQ: FaqItem[] = [
  {
    q: "How many eggs are in Palworld?",
    a: `Palworld has ${HATCHABLE_EGGS.length} hatchable eggs — Common, Large and Huge versions of ${EGG_FAMILIES.length} families (${EGG_FAMILIES.map((f) => f.family).join(", ")}) — plus the plain Egg, a food item produced by Chikipi on a Ranch that cannot be incubated.`,
  },
  {
    q: "How long do Palworld eggs take to hatch?",
    a: "At default settings a Common egg hatches in about 30 minutes, a Large egg in about 1 hour 10 minutes and a Huge egg in about 2 hours 30 minutes. The timer only advances while the egg sits in a placed Egg Incubator inside an active base.",
  },
  {
    q: "Which Palworld egg hatches Legendary Pals?",
    a: "Huge eggs. Huge Dragon eggs can hatch Jetragon, Huge Frozen eggs can hatch Frostallion, and Huge Dark eggs can hatch Necromus or Shadowbeak.",
  },
  {
    q: "Can you speed up egg hatching in Palworld?",
    a: "Yes. Lower PalEggDefaultHatchingTime in PalWorldSettings.ini (0.5 halves every timer, 0 hatches instantly), keep Frozen eggs cooled and Scorching eggs heated, and build several incubators so eggs hatch in parallel.",
  },
  {
    q: "Do wild eggs and bred eggs hatch the same Pals?",
    a: "No. A wild egg rolls randomly from its family and size pool, while a bred egg always hatches one fixed Pal determined by the parents' combined breed power — use the breeding calculator when you need a specific Pal.",
  },
  {
    q: "How to get Massive Huge Scorching Egg Palworld?",
    a: "Search volcano and desert egg routes, refresh the route after fast travel or sleep, and check every Scorching Egg nest repeatedly because a Massive Huge Scorching Egg is a rare wild spawn roll.",
  },
  {
    q: "Palworld Frozen Egg locations and hatch times?",
    a: "Frozen Eggs spawn most often in snow and high-altitude cold routes. Common Frozen Eggs hatch in about 30 minutes, Large Frozen Eggs in about 1 hour 10 minutes and Huge Frozen Eggs in about 2 hours 30 minutes on default settings.",
  },
];

export const Route = createFileRoute("/eggs")({
  head: () => ({
    meta: [
      { title: "Palworld Eggs — Every Egg, Hatch Time & Pool | Pal Stars" },
      { name: "description", content: `All ${HATCHABLE_EGGS.length} Palworld eggs with artwork, hatch times, spawn biomes, Frozen Egg locations and Huge Scorching Egg routes.` },
      { property: "og:title", content: "Palworld Eggs — Every Egg, Hatch Time & Hatch Pool" },
      { property: "og:description", content: "Common, Large and Huge Palworld eggs: where they spawn, how long they take and what hatches from each." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Palworld Eggs — Every Egg, Hatch Time & Hatch Pool" },
      { name: "twitter:description", content: "Every Palworld egg with hatch times, spawn biomes and hatch pools." },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqSchema(FAQ, URL),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Palworld eggs",
          numberOfItems: EGGS.length,
          itemListElement: EGGS.map((e, i) => ({ "@type": "ListItem", position: i + 1, name: e.name })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
            { "@type": "ListItem", position: 2, name: "Eggs", item: { "@id": URL, name: "Eggs" } },
          ],
        }),
      },
    ],
  }),
  component: EggsPage,
});

const SIZES: (EggSize | "All")[] = ["All", "Common", "Large", "Huge"];

function EggsPage() {
  const [size, setSize] = useState<EggSize | "All">("All");
  const [family, setFamily] = useState("All");

  const shown = useMemo(
    () =>
      EGGS.filter(
        (e) => (size === "All" || e.size === size) && (family === "All" || e.family === family),
      ),
    [size, family],
  );

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      {/* Visible breadcrumb trail comes from the site layout (src/components/Breadcrumbs.tsx). */}
      <h1 className="mt-3 text-3xl sm:text-4xl font-bold">Palworld Egg Hatching Guide: Breeding Pools &amp; Timer Charts</h1>
      <p className="aeo-answer text-muted-foreground mt-2 max-w-3xl">
        Palworld has {HATCHABLE_EGGS.length} hatchable eggs — Common, Large and Huge versions of{" "}
        {EGG_FAMILIES.length} families — plus the plain Egg food item. Common eggs hatch in about 30 minutes,
        Large eggs in about 1 hour 10 minutes and Huge eggs in about 2 hours 30 minutes.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ShareButton title="Palworld Eggs — every egg, hatch time & pool" text="Every Palworld egg with hatch times and hatch pools." path="/eggs" />
        <Link to="/guides/$slug" params={{ slug: "palworld-all-eggs-guide" }} className="text-sm text-primary hover:underline">
          Read the full eggs guide →
        </Link>
      </div>

      <section className="mt-6 glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">How to Use the Breeding Calculator for Rare Egg Combinations</h2>
        <p className="aeo-answer mt-2 max-w-3xl text-sm text-muted-foreground">
          Huge eggs hatch the rare pools, but you control which Pal you get by breeding rather than by hunting nests. Put
          the two parents whose combined breed power averages closest to your target into a Breeding Farm with a Grade S
          Cake, then incubate. The{" "}
          <Link to="/breeding" className="text-primary hover:underline">Palworld breeding calculator</Link>{" "}
          lists every valid parent pairing for each Pal that appears in the egg pools below.
        </p>
      </section>

      <div className="mt-6 flex flex-wrap gap-2">
        {SIZES.map((s) => (
          <button key={s} onClick={() => setSize(s)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${size === s ? "bg-primary text-primary-foreground" : "glass hover:brightness-110"}`}>
            {s === "All" ? "All sizes" : `${s} eggs`}
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {["All", ...EGG_FAMILIES.map((f) => f.family), "Food"].map((f) => (
          <button key={f} onClick={() => setFamily(f)}
            className={`rounded-full px-3 py-1.5 text-xs ${family === f ? "bg-primary text-primary-foreground" : "glass hover:brightness-110"}`}>
            {f === "All" ? "All families" : f}
          </button>
        ))}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Showing {shown.length} of {EGGS.length} eggs. Egg illustrations are original Pal Stars artwork tinted per
        element — Palworld's own egg art is Pocketpair intellectual property.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-6">
        {shown.map((e) => (
          <article key={e.slug} className="glass rounded-2xl p-5">
            <div className="mx-auto max-w-[160px]">
              <EggArt egg={e} size={160} />
            </div>
            <h2 className="mt-3 text-lg font-semibold">{e.name}</h2>
            <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-widest">
              <span className="rounded-full px-2 py-0.5" style={{ background: e.accent + "33", color: e.accent }}>{e.element}</span>
              <span className="rounded-full glass px-2 py-0.5">{e.size}</span>
              <span className="rounded-full glass px-2 py-0.5">{e.rarity}</span>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{e.desc}</p>
            <dl className="mt-3 text-xs space-y-1">
              <div className="flex gap-2"><dt className="text-muted-foreground w-20">Hatch time</dt><dd className="font-medium">{e.hatchTime}</dd></div>
              <div className="flex gap-2"><dt className="text-muted-foreground w-20">Found in</dt><dd className="font-medium">{e.biome}</dd></div>
              {e.hatches.length > 0 && (
                <div className="flex gap-2"><dt className="text-muted-foreground w-20">Hatches</dt><dd className="font-medium">{e.hatches.join(", ")}</dd></div>
              )}
            </dl>
            {!e.food && (
              <Link to="/guides/$slug" params={{ slug: `palworld-${e.slug}-guide` }} className="mt-3 inline-block text-sm text-primary hover:underline">
                {e.name} guide →
              </Link>
            )}
          </article>
        ))}
      </div>

      <InternalLinkBlock path="/eggs" />
      <FaqSection items={FAQ} heading="Palworld eggs FAQ" />

      <RelatedGuides
        links={[
          { slug: "palworld-all-eggs-guide", label: "All Palworld Eggs Guide & FAQ" },
          { slug: "palworld-huge-dragon-egg-guide", label: "Huge Dragon Egg guide" },
          { slug: "palworld-huge-frozen-egg-guide", label: "Huge Frozen Egg guide" },
          { slug: "breeding", label: "Palworld breeding guide" },
        ]}
      />
    </div>
  );
}
